/**
 * User interface preferences that survive a reload.
 *
 * Panel widths are stored as percentages of the workspace so the three-column
 * layout keeps the same proportions on desktop, tablet and phone viewports.
 * Engineering geometry is never stored here.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

const STORAGE_PREFIX = 'ejot-roofing.';
const PANEL_PERCENT_STORAGE_VERSION = 'v2';

export type Theme = 'light' | 'dark';

function readStored(key: string): string | null {
  try {
    return window.localStorage.getItem(STORAGE_PREFIX + key);
  } catch {
    return null;
  }
}

function writeStored(key: string, value: string): void {
  try {
    window.localStorage.setItem(STORAGE_PREFIX + key, value);
  } catch {
    // Preferences are optional convenience data. Hardened browser profiles may
    // reject local storage without affecting project or engineering state.
  }
}

function applyTheme(theme: Theme): void {
  document.documentElement.setAttribute('data-theme', theme);
  writeStored('theme', theme);
}

export function useTheme(): [Theme, () => void] {
  const [theme, setTheme] = useState<Theme>(() => (readStored('theme') === 'dark' ? 'dark' : 'light'));
  const current = useRef(theme);

  useLayoutEffect(() => {
    applyTheme(current.current);
  }, []);

  const toggle = useCallback(() => {
    const next: Theme = current.current === 'light' ? 'dark' : 'light';
    current.current = next;
    applyTheme(next);
    setTheme(next);
  }, []);

  return [theme, toggle];
}

export interface PanelWidths {
  /** Width as a percentage of the complete workspace width. */
  left: number;
  /** Width as a percentage of the complete workspace width. */
  right: number;
}

export interface PanelVisibility {
  left: boolean;
  right: boolean;
}

export const PANEL_LIMITS = {
  leftMin: 5,
  leftMax: 45,
  rightMin: 5,
  rightMax: 45,
} as const;

/** Default layout: 15% Input + 70% Graphics + 15% Results. */
export const DEFAULT_PANEL_WIDTHS: PanelWidths = {
  left: 15,
  right: 15,
};

export const DEFAULT_PANEL_VISIBILITY: PanelVisibility = {
  left: true,
  right: true,
};

export function clampLeft(width: number): number {
  return clampFinite(width, PANEL_LIMITS.leftMin, PANEL_LIMITS.leftMax, DEFAULT_PANEL_WIDTHS.left);
}

export function clampRight(width: number): number {
  return clampFinite(width, PANEL_LIMITS.rightMin, PANEL_LIMITS.rightMax, DEFAULT_PANEL_WIDTHS.right);
}

function clampFinite(width: number, min: number, max: number, fallback: number): number {
  if (!Number.isFinite(width)) return fallback;
  return Math.min(max, Math.max(min, width));
}

function storedWidth(key: string, fallback: number, clamp: (width: number) => number): number {
  const raw = readStored(key);
  if (raw === null || raw.trim() === '') return fallback;
  return clamp(Number(raw));
}

function percentageStorageKey(side: 'left' | 'right'): string {
  return `panel.${side}.percent.${PANEL_PERCENT_STORAGE_VERSION}`;
}

export function usePanelLayout() {
  const [widths, setWidths] = useState<PanelWidths>(() => ({
    // Versioned percentage keys intentionally ignore legacy pixel widths.
    left: storedWidth(percentageStorageKey('left'), DEFAULT_PANEL_WIDTHS.left, clampLeft),
    right: storedWidth(percentageStorageKey('right'), DEFAULT_PANEL_WIDTHS.right, clampRight),
  }));
  const [visible, setVisible] = useState<PanelVisibility>(() => ({
    left: readStored('panel.left.visible') !== 'false',
    right: readStored('panel.right.visible') !== 'false',
  }));

  useEffect(() => {
    writeStored(percentageStorageKey('left'), String(widths.left));
    writeStored(percentageStorageKey('right'), String(widths.right));
  }, [widths]);

  useEffect(() => {
    writeStored('panel.left.visible', String(visible.left));
    writeStored('panel.right.visible', String(visible.right));
  }, [visible]);

  const setLeftWidth = useCallback((width: number) => {
    setWidths((previous) => ({ ...previous, left: clampLeft(width) }));
  }, []);

  const setRightWidth = useCallback((width: number) => {
    setWidths((previous) => ({ ...previous, right: clampRight(width) }));
  }, []);

  const toggleLeft = useCallback(() => setVisible((value) => ({ ...value, left: !value.left })), []);
  const toggleRight = useCallback(() => setVisible((value) => ({ ...value, right: !value.right })), []);

  const reset = useCallback(() => {
    setWidths(DEFAULT_PANEL_WIDTHS);
    setVisible(DEFAULT_PANEL_VISIBILITY);
  }, []);

  return { widths, visible, setLeftWidth, setRightWidth, toggleLeft, toggleRight, reset };
}
